
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { toast } from "sonner";
import { GearItem } from "@/types/gear";

export function useGearItems(projectId: string) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!projectId) return;

    const gearChannel = supabase
      .channel('gear-items-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'gear_items',
          filter: `project_id=eq.${projectId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["gear-items", projectId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(gearChannel);
    };
  }, [projectId, queryClient]);

  return useQuery({
    queryKey: ["gear-items", projectId],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from("gear_items")
          .select("*, vendors(name)")
          .eq("project_id", projectId);

        if (error) {
          console.error("Error fetching gear items:", error);
          toast.error("Failed to load gear items");
          throw error;
        }

        // Sort the data by category after fetching
        const sortedData = data?.sort((a, b) => {
          if (a.category < b.category) return -1;
          if (a.category > b.category) return 1;
          return 0;
        }) || [];

        return sortedData;
      } catch (error) {
        console.error("Unexpected error:", error);
        toast.error("An unexpected error occurred while loading gear items");
        throw error;
      }
    },
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true
  });
}
